import { useEffect, useState } from 'react';
import { format } from 'date-fns';
//
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import Input, { InputProps, inputClasses } from '@mui/material/Input';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
//
import { useSnackbar } from 'src/components/snackbar';
import { useServerRequest } from 'src/hooks/useServerRequest';
import { useLocales } from 'src/locales';
import { useDispatch, useSelector } from 'react-redux';
import { addIssuedCheck } from 'src/redux/slices/dataSlice';
import Autocomplete from '@mui/material/Autocomplete';
import { selectFilteredData } from 'src/redux/selectors';
import { getBeneficiairesFromChecks } from 'src/utils/checks/get-benefeceires';

const STEP = 50;
const MIN_AMOUNT = 0;
const MAX_AMOUNT = 1000;

interface InputAmountProps extends InputProps {
    autoWidth: number;
    amount: number | number[];
    onAmountChange: (amount: number | number[]) => void;
}

function InputAmount({ autoWidth, amount, onBlur, onAmountChange, sx, ...other }: InputAmountProps) {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = Number(event.target.value);
        onAmountChange(newValue);
    };
    return (
        <Stack direction="row" justifyContent="center" spacing={1} sx={sx}>
            <Typography variant="h5">DH</Typography>

            <Input
                disableUnderline
                size="small"
                value={amount}
                onChange={handleChange}
                onBlur={onBlur}
                inputProps={{ step: STEP, min: MIN_AMOUNT, max: MAX_AMOUNT, type: 'number' }}
                sx={{
                    [`& .${inputClasses.input}`]: {
                        p: 0,
                        typography: 'h3',
                        textAlign: 'center',
                        width: autoWidth,
                    },
                }}
                {...other}
            />
        </Stack>
    );
}
// ----------------------------------------------------------------------
interface QuickIssuedProps {
    open: boolean;
    amount: number | number[];
    company: number | null;
    rib: number | null;
    carnet: number | null;
    check: string | null;
    onClose: VoidFunction;
}

function QuickIssued({
    open,
    amount,
    company,
    rib,
    carnet,
    check,
    onClose,
}: QuickIssuedProps) {
    const filteredData = useSelector(selectFilteredData);
    const listBenefecieries = getBeneficiairesFromChecks(filteredData);
    const [beneficiary, setBeneficiary] = useState<string>('');
    const [takenDate, setTakenDate] = useState<Date | null>(new Date());
    const [dueDate, setDueDate] = useState<Date | null>(null);
    const [bill, setBill] = useState<string>('');
    const [note, setNote] = useState<string>('');
    const [currentAmount, setCurrentAmount] = useState<number | number[]>(amount);
    const [autoWidths, setAutoWidths] = useState(24);
    const dispatch = useDispatch();
    const { t } = useLocales();
    const { enqueueSnackbar } = useSnackbar();
    const { makeRequest } = useServerRequest();
    // Calculate autoWidths only when currentAmount changes
    useEffect(() => {
        const getNumberLength = currentAmount.toString().length;
        setAutoWidths(getNumberLength * 24);
    }, [currentAmount]);
    const handleSubmit = async () => {
        const formattedTakenDate = takenDate ? format(takenDate, 'yyyy-MM-dd HH:mm:ss') : null;
        const formattedDueDate = dueDate ? format(dueDate, 'yyyy-MM-dd HH:mm:ss') : null;

        const data = {
            company_id: company,
            rib_id: rib,
            carnet_id: carnet,
            check_id: check,
            benificier_id: beneficiary,
            amount: currentAmount,
            taken_date: formattedTakenDate,
            due_date: formattedDueDate, // optional
            facture: bill,   // optional
            remarque: note,  // optional
        };

        if (!data.company_id) {
            enqueueSnackbar(t('common.input.company.error1'), { variant: 'error' });
            return;
        }
        if (!data.rib_id) {
            enqueueSnackbar(t('common.input.rib.error2'), { variant: 'error' });
            return;
        }
        if (!data.carnet_id) {
            enqueueSnackbar(t('common.input.carnet.error1'), { variant: 'error' });
            return;
        }
        if (!data.check_id) {
            enqueueSnackbar(t('common.input.check.error1'), { variant: 'error' });
            return;
        }
        if (!data.benificier_id) {
            enqueueSnackbar(t('common.input.benef.error1'), { variant: 'error' });
            return;
        }
        if (!data.amount || Number.isNaN(Number(data.amount))) {
            enqueueSnackbar(t('common.input.amount.error1'), { variant: 'error' });
            return;
        }
        if (!data.taken_date) {
            enqueueSnackbar(t('common.input.takenDate.error1'), { variant: 'error' });
            return;
        }
        const response = await makeRequest(`/saveCheck`, {
            method: 'POST',
            data,
        }, t('common.alert.check'));

        if (response.success && response.data) {
            dispatch(addIssuedCheck({
                companyId: data.company_id,
                ribId: data.rib_id,
                carnetId: data.carnet_id,
                check: response.data.check,
            }))
            onClose();
        }
    };

    return (
        <Dialog open={open} disableEscapeKeyDown fullWidth maxWidth="xs" onClose={(_, reason) => reason !== 'backdropClick' && onClose()}>
            <DialogTitle>{t('m.t.completCheck')}</DialogTitle>

            <Stack spacing={3} sx={{ px: 3 }}>

                <InputAmount
                    amount={currentAmount}  // Use state here
                    onAmountChange={setCurrentAmount}  // Pass the handler
                    autoWidth={autoWidths}
                    disableUnderline={false}
                    sx={{ justifyContent: 'flex-end' }}
                />

                <Grid container spacing={2} sx={{ pt: '10px' }}>
                    <Grid item xs={12}>
                        <Autocomplete
                            fullWidth
                            freeSolo
                            options={listBenefecieries}
                            getOptionLabel={(option) => option}
                            value={beneficiary || ''}
                            onInputChange={(event, newValue) => setBeneficiary(newValue.toUpperCase())}
                            renderInput={(params) => <TextField {...params} label={t('common.input.benef.label')} />}
                            renderOption={(props, option) => (
                                <li {...props} key={option}>
                                    {option}
                                </li>
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DatePicker
                            label={t('common.input.takenDate.label')}
                            value={takenDate || null}
                            onChange={(newValue) => setTakenDate(newValue)}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <DatePicker
                            label={t('common.input.dueDate.label')}
                            value={dueDate || null}
                            onChange={(newValue) => setDueDate(newValue)}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('common.input.bill.label')}
                            value={bill || ''}
                            onChange={(e) => setBill(e.target.value)}
                        />
                    </Grid>
                </Grid>
                <TextField fullWidth multiline rows={3} onChange={(e) => setNote(e.target.value)} placeholder={t('common.input.note.label')} />
            </Stack>

            <DialogActions>
                <Button onClick={onClose}>
                    {t('common.button.action.cancel')}
                </Button>

                <Button variant="contained" disabled={amount === 0} onClick={handleSubmit}>
                    {t('common.button.action.submit')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default QuickIssued;